import { useTranslation } from 'react-i18next';
import { useMedia } from 'react-use';

import { screen } from 'components/styles/variables';
import directions from 'public/static/images/o2o/directions.svg';
import phone from 'public/static/images/o2o/phone.svg';
import website from 'public/static/images/o2o/website.svg';

import styles from './styles';

function VehicleCard({
  status,
  price,
  availability,
  priceRequest,
  acceptedPrice,
  year,
  make,
  model,
  trim,
  askingPrice,
  mileage,
  lastUpdated,
  dealer,
}: {
  status: string;
  price?: string;
  availability: string;
  priceRequest?: string;
  acceptedPrice?: string;
  year: string;
  make: string;
  model: string;
  trim: string;
  askingPrice: string;
  mileage: string;
  lastUpdated: string;
  dealer: {
    name: string;
    phone: string;
    addressLine1: string;
    addressLine2: string;
  };
}): JSX.Element {
  const { t } = useTranslation();
  const isDesktop = useMedia(`(min-width: ${screen.smMin})`);

  function getStatusText(): string {
    switch (status) {
      case 'default':
        return t('o2o.contacted.statusText.default');
      case 'available':
        return t('o2o.contacted.statusText.available');
      case 'priceConfirmed':
        return t('o2o.contacted.statusText.priceConfirmed');
      case 'unavailable':
        return t('o2o.contacted.statusText.unavailable');
      case 'priceDropRequested':
        return t('o2o.contacted.statusText.priceDropRequested');
      case 'availablePriceDropAccepted':
        return t('o2o.contacted.statusText.availablePriceDropAccepted');
      default:
        return t('o2o.contacted.statusText.default');
    }
  }

  function getAvailabilityText(): string {
    switch (availability) {
      case 'available':
        return t('o2o.contacted.availabilityText.available');
      case 'pending':
        return t('o2o.contacted.availabilityText.pending');
      case 'unavailable':
        return t('o2o.contacted.availabilityText.unavailable');
      default:
        return '';
    }
  }

  function getPriceText(): string {
    switch (price) {
      case 'accepted':
        return t('o2o.contacted.priceText.accepted');
      case 'pending':
        return t('o2o.contacted.priceText.pending');
      case 'rejected':
        return t('o2o.contacted.priceText.rejected');
      default:
        return price || '';
    }
  }

  function getPriceClassName(): string {
    switch (price) {
      case 'accepted':
        return 'price-accepted';
      case 'rejected':
        return 'price-rejected';
      case 'pending':
        return 'price-pending';
      default:
        return 'price-confirmed';
    }
  }

  function getStatusClassName(): string {
    switch (availability) {
      case 'available':
        return 'available';
      case 'unavailable':
        return 'unavailable';
      default:
        return '';
    }
  }

  return (
    <>
      <div className="vehicle-card">
        <div className={`header ${getStatusClassName()}`}>
          <div className="status">{getStatusText()}</div>
          <div className="date">{lastUpdated}</div>
        </div>
        <div className="content">
          <div className="vehicle-information">
            <div className="flex">
              <div>
                <img src="https://placehold.co/376x223" alt="Vehicle" className="vehicle-image" />
              </div>
              <div className="vehicle-info">
                <div className="vehicle-name">
                  {year} {make} {model} {trim}
                </div>
                <div className="vehicle-mileage">
                  {mileage} {t('o2o.contacted.miles')}
                </div>
                <div className={`vehicle-price ${acceptedPrice ? 'accepted-price' : ''}`}>
                  <span className={`${acceptedPrice ? 'accepted-price-original' : ''}`}>
                    {askingPrice}
                  </span>{' '}
                  {acceptedPrice ? <span className="accepted-price">{acceptedPrice}</span> : null}
                  {priceRequest ? (
                    <span className="requested-price">
                      {t('o2o.contacted.requested')} {priceRequest}
                    </span>
                  ) : null}
                </div>
                <div className="vehicle-availability">
                  <span>{t('o2o.contacted.availability')}</span>{' '}
                  <span className={`status-text ${availability}`}>{getAvailabilityText()}</span>
                </div>
                <div className={`vehicle-current-price ${availability === 'unavailable'}`}>
                  <span>{t('o2o.contacted.price')}</span>
                  <span className={`status-text ${getPriceClassName()}`}>{getPriceText()}</span>
                </div>
                {availability === 'unavailable' ? (
                  <div>
                    <button className="remove-button">{t('o2o.contacted.remove')}</button>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          <div className="dealer-information">
            <div className="dealer-name">{dealer.name}</div>
            <div className="dealer-address-container">
              <div className="dealer-address-1">{dealer.addressLine1}</div>
              <div className="dealer-address-2">{dealer.addressLine2}</div>
            </div>
            <div className="dealer-contact">
              <div className="dealer-phone">
                <img className="contact-icon" src={phone} />{' '}
                {isDesktop ? dealer.phone : t('o2o.contacted.call')}
              </div>
              <div
                className="dealer-directions"
                onClick={(): void => console.log('TAKE ME AWAAAY')}
              >
                <img className="contact-icon" src={directions} />{' '}
                {isDesktop ? t('o2o.contacted.directions') : t('o2o.contacted.directionsMobile')}
              </div>
              <div className="dealer-website" onClick={(): void => console.log('TAKE ME AWAAAY')}>
                <img className="contact-icon" src={website} style={{ padding: '7px' }} />{' '}
                {t('o2o.contacted.website')}
              </div>
            </div>
          </div>
        </div>
      </div>
      <style>{styles}</style>
    </>
  );
}

export default VehicleCard;
